import React, { useState } from 'react';
import cn from 'clsx';

import Carousel, { Slide } from '../../library/carousel/carousel';
import Image from '../../library/image/image';
import ProductImageGalleryModal from './components/product-image-gallery-modal/product-image-gallery-modal';
import ProductSecondaryFlags from '../product-secondary-flags/product-secondary-flags';
import WebOnlyFlag from '../web-only-flag/web-only-flag';

import { useMediaQuery, useMyAccount } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { isWebOnlyProduct } from '../../shared/utils/product/product';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  Maybe,
  ProductData,
  SwatchImageData
} from '../../shared/types/graphql-types';
import { PRODUCT_IMAGE_FORMATS } from '../../shared/constants/product-images';

import styles from './product-image-gallery.scss';

export interface ImageInterface {
  altText: string;
  secondaryAltText?: string;
  secondaryUrl?: string;
  url: string;
}

export interface GalleryImages {
  product?: ImageInterface[];
  thumbnail?: ImageInterface[];
  zoom?: ImageInterface[];
}

interface ProductImageGalleryProps {
  fallbackProductImage: string;
  galleryImages: GalleryImages;
  height?: string;
  product?: Maybe<ProductData>;
  selectedSwatch?: SwatchImageData;
  showFallbackProductImage?: boolean;
  width?: string;
}

function ProductImageGallery(props: ProductImageGalleryProps) {
  const {
    fallbackProductImage,
    galleryImages,
    height = '300',
    product,
    selectedSwatch,
    showFallbackProductImage,
    width = '300'
  } = props;

  const [imageIdx, setImageIdx] = useState(0);
  const [modalImage, setModalImage] = useState<ImageInterface | null>(null);
  const [show360View, setShow360View] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const breakpoint = useMediaQuery();
  const { isFleetAccount } = useMyAccount();
  const isWebOnly = isWebOnlyProduct(product);

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const productCode =
    selectedSwatch?.productCode ??
    product?.aggregates?.codes?.[0] ??
    product?.code;

  //TODO: Use dot notation once this is available in all environments
  const is360ViewAvailable = product?.['is360ViewAvailable'];

  const productImages = Array.isArray(
    galleryImages?.[PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM]
  )
    ? [...galleryImages[PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM]]
    : [];

  const thumbnailImages = Array.isArray(
    galleryImages?.[PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL]
  )
    ? [...galleryImages[PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL]]
    : [];

  if (
    is360ViewAvailable &&
    productImages.length > 0 &&
    thumbnailImages.length > 0
  ) {
    productImages.push(productImages[0]);
    thumbnailImages.push(thumbnailImages[0]);
  }

  function openImageModal(imageIdx) {
    let zoomImage: ImageInterface | null;
    zoomImage = Array.isArray(
      galleryImages?.[PRODUCT_IMAGE_FORMATS.PRODUCT_LARGE]
    )
      ? galleryImages[PRODUCT_IMAGE_FORMATS.PRODUCT_LARGE][imageIdx]
      : null;

    if (zoomImage) {
      setModalImage(zoomImage);
      setIsImageModalOpen(true);
    } else if (is360ViewAvailable) {
      setIsImageModalOpen(true);
      setShow360View(true);
      trackAction('product_wheels_360_degree_click');
    }
  }

  function closeImageModal() {
    setIsImageModalOpen(false);
    setModalImage(null);
    setShow360View(false);
  }

  return (
    <div
      className={cn(
        showFallbackProductImage
          ? styles['product-fallback-container']
          : styles['product-carousel-container'],
        'product-image-gallery'
      )}
    >
      {isWebOnly && <WebOnlyFlag className={styles['web-only-flag']} />}
      {isArrayWithLength(productImages) && (
        <div
          className={cn(
            styles['product-carousel'],
            'product-image-gallery__product-carousel'
          )}
        >
          <Carousel
            hasFullWidthSlides
            onSlideChange={setImageIdx}
            showIndicators={breakpoint.isXS}
            slideIndex={imageIdx}
          >
            {productImages.map((image, idx) => (
              <Slide
                className={styles['product-image-wrapper']}
                key={`gi-${idx}`}
                shouldLazyLoad
              >
                {isFleetEnabled && (
                  <ProductSecondaryFlags hideWebOnly product={product} />
                )}
                <button
                  className={styles['product-carousel-slide']}
                  onClick={() => {
                    openImageModal(idx);
                  }}
                >
                  <Image
                    alt={image.altText}
                    className="product-image-gallery__product-carousel-image"
                    height={height}
                    src={image.url}
                    title={image.altText}
                    width={width}
                  />

                  {is360ViewAvailable && idx === productImages.length - 1 && (
                    <img
                      alt="360 degree icon"
                      className={styles['slide-360-icon']}
                      src="https://discounttire.scene7.com/is/content/discounttire/logos/icons/360-view-icon.svg"
                    />
                  )}
                </button>
              </Slide>
            ))}
          </Carousel>
        </div>
      )}

      {showFallbackProductImage && (
        <div
          className={styles['product-fallback-image']}
          style={{
            backgroundImage: `url(${fallbackProductImage})`,
            height: '190px'
          }}
        >
          {isFleetEnabled && (
            <ProductSecondaryFlags hideWebOnly product={product} />
          )}
        </div>
      )}

      {isArrayWithLength(thumbnailImages) && breakpoint.isMinSm && (
        <div
          className={cn(
            styles['product-carousel-thumbnails'],
            'product-image-gallery__product-carousel-thumbnails'
          )}
        >
          {thumbnailImages.map((img, idx) => (
            <button
              className={cn(
                idx === imageIdx
                  ? styles['product-carousel-thumbnail-selected']
                  : styles['product-carousel-thumbnail'],
                idx === imageIdx
                  ? 'product-image-gallery__product-carousel-thumbnail-selected'
                  : 'product-image-gallery__product-carousel-thumbnail'
              )}
              key={`thumbnail-${idx}`}
              onClick={() => setImageIdx(idx)}
            >
              <img
                alt={img.altText}
                height="68"
                src={img.url}
                title={img.altText}
                width="68"
              />

              {is360ViewAvailable && idx === thumbnailImages.length - 1 && (
                <div className={styles['thumbnail-360-icon']}>
                  <img
                    alt="360 degree icon"
                    src="https://discounttire.scene7.com/is/content/discounttire/logos/icons/360-view-icon.svg"
                  />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {isImageModalOpen && (
        <ProductImageGalleryModal
          image={modalImage}
          isOpen
          onClose={closeImageModal}
          productCode={productCode}
          show360View={show360View}
        />
      )}
    </div>
  );
}

export default ProductImageGallery;
